import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Eye, Clock, Flame, Trophy, HandHeart, Target,
  BookOpenText, Swords, ChevronRight, Activity,
} from 'lucide-react';
import api from '@/lib/api';

/**
 * Phase 2N PR 2 — Per-Squad Feed (Heartbeat of the crew).
 *
 * Quiet ambient signals + loud kinetic moments composed into one tribal
 * feed. Renders inside the Squad Detail "Feed" tab. Tone is warm + tribal,
 * never analytics-speak — see narrative_style_guide.md.
 */

const KIND_ICON = {
  squad_teammates_online: Eye,
  squad_teammates_watching: Eye,
  squad_upcoming_game: Clock,
  squad_upcoming_rivalry: Flame,
  squad_crew_hype: HandHeart,
  squad_mvp_moment: Trophy,
  squad_rivalry_result: Swords,
  squad_story_replayed: BookOpenText,
  squad_story_about_us: BookOpenText,
};

const KIND_TONE = {
  squad_teammates_watching: 'border-emerald-500/30 bg-gradient-to-r from-emerald-500/8 to-transparent',
  squad_upcoming_rivalry: 'border-rose-500/30 bg-gradient-to-r from-rose-500/8 to-transparent',
  squad_upcoming_game: 'border-cyan-500/25 bg-gradient-to-r from-cyan-500/8 to-transparent',
  squad_crew_hype: 'border-amber-500/30 bg-gradient-to-r from-amber-500/8 to-transparent',
  squad_teammates_online: 'border-slate-700 bg-slate-900/40',
  squad_rivalry_result: 'border-slate-700 bg-slate-900/40',
  squad_mvp_moment: 'border-amber-500/25 bg-gradient-to-r from-amber-500/6 to-transparent',
  squad_story_about_us: 'border-purple-500/25 bg-gradient-to-r from-purple-500/8 to-transparent',
  squad_story_replayed: 'border-slate-800 bg-slate-900/20',
};

const KIND_LABEL = {
  squad_teammates_online: 'CREW ONLINE',
  squad_teammates_watching: 'CREW WATCHING',
  squad_upcoming_game: 'NEXT UP',
  squad_upcoming_rivalry: 'RIVALRY KICKOFF',
  squad_crew_hype: 'CREW LOCKING IN',
  squad_mvp_moment: 'MVP NOD',
  squad_rivalry_result: 'RECENT RESULT',
  squad_story_replayed: 'STORY REPLAYED',
  squad_story_about_us: 'NEW STORY',
};

export default function ArenaxSquadFeed({ squadId, limit = 16 }) {
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);

  const reload = () => {
    if (!squadId) return;
    setLoading(true);
    api.get(`/arenax/squads/${squadId}/feed?limit=${limit}`)
      .then(r => setData(r.data || { items: [] }))
      .catch(() => setData({ items: [] }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    reload();
    // Light auto-refresh every 35s so quiet signals show up without a hard reload
    const t = setInterval(reload, 35000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [squadId]);

  if (loading && (data.items || []).length === 0) return null;
  if (!data || (data.items || []).length === 0) {
    return (
      <div className="rounded-2xl border border-slate-800 p-6 text-center"
        data-testid="arenax-squad-feed-empty"
        style={{ background: 'linear-gradient(160deg, var(--ax-card-bg-soft), var(--ax-card-bg-soft))' }}>
        <Activity className="h-5 w-5 text-slate-500 mx-auto mb-2" />
        <div className="text-sm text-slate-400">The crew's heartbeat will show up here. Play a game together and watch this feed come alive.</div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-800 overflow-hidden"
      data-testid="arenax-squad-feed"
      style={{ background: 'linear-gradient(160deg, var(--ax-card-bg-soft), var(--ax-card-bg-soft))' }}>
      <div className="px-5 pt-5 pb-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-cyan-300" />
          <span className="text-[10px] tracking-[0.3em] text-cyan-300 font-bold uppercase">Squad heartbeat</span>
        </div>
        <div className="mt-2 text-sm text-slate-400">Quiet signals matter. Loud ones too.</div>
      </div>
      <div className="p-3 space-y-2">
        {data.items.map((item, i) => (
          <SquadFeedRow key={`${item.kind}_${i}`} item={item} />
        ))}
      </div>
    </div>
  );
}

function SquadFeedRow({ item }) {
  const Icon = KIND_ICON[item.kind] || Activity;
  const tone = KIND_TONE[item.kind] || 'border-slate-800 bg-slate-900/40';
  const label = KIND_LABEL[item.kind] || 'UPDATE';

  const content = (
    <div className={`rounded-xl border ${tone} px-4 py-3 transition-all hover:translate-x-0.5`}
      data-testid={`arenax-squad-feed-item-${item.kind}`}>
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <Icon className="h-4 w-4 text-slate-200/80" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.25em] text-slate-500 font-bold uppercase mb-1">{label}</div>
          <div className="text-[14px] text-slate-100 leading-snug">{item.narrative}</div>
          {item.hours_to_go !== undefined && item.hours_to_go !== null && item.hours_to_go > 0 && (
            <div className="mt-1 text-[11px] text-amber-300/80 font-mono">
              ⏱ in {item.hours_to_go < 1 ? '<1' : Math.round(item.hours_to_go)}h
            </div>
          )}
        </div>
        {item.deeplink && <ChevronRight className="h-4 w-4 text-slate-500 shrink-0 mt-1" />}
      </div>
    </div>
  );
  if (item.deeplink) {
    return <Link to={item.deeplink} className="block">{content}</Link>;
  }
  return content;
}
